import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {
  addDoc,
  collection,
  deleteDoc,
  doc, getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query, startAfter, startAt,
  updateDoc, where,
  deleteField
} from 'firebase/firestore';
import {first, last, uniq} from 'lodash';
import {addCompIdsToCart, clearCart} from '../cart/state';
import {Field} from '../model/fields';
import {List, User} from '../model/objects';
import {MODE_CREATE} from './constants';

export const loadLists = createAsyncThunk(
  'list/load',
  async (arg, {getState, rejectWithValue}) => {
    const org = getState().auth.org.id;
    try {
      const result = await getDocs(query(
        collection(getFirestore(), 'orgs', org, 'lists'),
        orderBy(Field.UPDATED.path, "desc"),
        limit(25),
      ));
      return result.docs.map(List.create)
    }
    catch(err) {
      return rejectWithValue(err);
    }
  }
);

export const addList = createAsyncThunk(
  'list/add',
  async (arg, {getState, rejectWithValue}) => {
    const {cart, list, auth} = getState();
    try {
      const u = {
        uid: auth.user.uid,
        email: auth.user.email,
        name: auth.user.displayName
      }
      const newList = {
        ...list.curr,
        comps: cart.comps.map(c => c.id),
        users: [auth.user.uid],
        created: new Date(),
        created_by: u,
        updated: new Date(),
        updated_by: u,
      };

      await addDoc(collection(getFirestore(), 'orgs', auth.org.id, 'lists'), newList);
      return newList;
    }
    catch(err) {
      return rejectWithValue(err);
    }
  }
);

export const saveList = createAsyncThunk(
  'list/save',
  async (arg, {getState, rejectWithValue}) => {
    const {auth, list} = getState();
    try {
      const users = [...(list.users||[])];
      if (!users.includes(auth.user.uid)) {
        users.push(auth.user.uid);
      }

      const newList = {
        ...list.curr,
        users,
        updated: new Date(),
        updated_by: {
          email: auth.user.email,
          name: auth.user.displayName
        }
      };

      await updateDoc(doc(getFirestore(), 'orgs', auth.org.id, 'lists', list.curr.id), newList);
      return newList;
    }
    catch(err) {
      return rejectWithValue(err);
    }
  }
);

export const updateList = createAsyncThunk(
  'list/update',
  async (replace, {getState, rejectWithValue}) => {
    const {auth, list, cart} = getState();
    try {
      const newComps = cart.comps.map(c => c.id);
      const newList = {
        ...list.curr,
        comps: replace ? newComps : uniq((list.curr.comps||[]).concat(newComps)),
        updated: new Date()
      };

      await updateDoc(doc(getFirestore(), 'orgs', auth.org.id, 'lists', list.curr.id), newList);
      return newList;
    }
    catch(err) {
      return rejectWithValue(err);
    }
  }
);

export const deleteList = createAsyncThunk(
  'list/delete',
  async (list, {getState, rejectWithValue}) => {
    const org = getState().auth.org.id;
    try {
      await deleteDoc(doc(getFirestore(), 'orgs', org, 'lists', list.id));
      return list;
    }
    catch(err) {
      return rejectWithValue(err);
    }
  }
);

const lookupTeamMember = async (org, email) => {
  const userResults = await getDocs(query(collection(getFirestore(), 'orgs', org, 'users'), where("email", "==", email)));
  if (userResults.size > 0) {
    const userDoc = userResults.docs[0];
    return {
      uid: userDoc.get('uid'),
      email,
      displayName: `${userDoc.get('first_name')} ${userDoc.get('last_name')}`
    }
  }
  else {
    throw new Error("Unable to find user with email "+email);
  }
}

export const assignList = createAsyncThunk(
    'list/assign',
    async ({list, user, email}, {getState, rejectWithValue}) => {
      const org = getState().auth.org.id;
      try {
        if (!user) {
          user = await lookupTeamMember(org, email);
        }

        let users = list.users || [];
        if (!users.includes(user.uid)) {
          users = [...users, user.uid];
        }

        const update = {
          users,
          created_by: {
            uid: user.uid,
            email: user.email,
            name: user.displayName
          },
          updated_by: {
            uid: user.uid,
            email: user.email,
            name: user.displayName
          }
        };

        await updateDoc(doc(getFirestore(), 'orgs', org, 'lists', list.id), update);
        return {
          ...list,
          ...update
        };
      }
      catch(err) {
        console.log(err);
        return rejectWithValue(err);
      }
    }
);

export const unassignList = createAsyncThunk(
    'list/unassign',
    async ({list, user, email}, {getState, rejectWithValue}) => {
      const {auth} = getState();
      const org = auth.org.id;
      try {
        if (!user) {
          user = await lookupTeamMember(org, email);
        }

        let users = list.users || [];
        if (users.includes(user.uid)) {
          users = users.filter(it => it !== user.uid);
        }

        const update = { users };

        if (list.created_by?.uid === auth.user.uid || list.created_by?.email === auth.user.email) {
          update.created_by = deleteField();
          update.updated_by = deleteField();
        }

        await updateDoc(doc(getFirestore(), 'orgs', org, 'lists', list.id), update);
        return {
          ...list,
          ...update
        };
      }
      catch(err) {
        console.log(err);
        return rejectWithValue(err);
      }
    }
);


export const shareList = createAsyncThunk(
    'list/share',
    async ({list, email}, {getState, rejectWithValue}) => {
      const org = getState().auth.org.id;
      try {
        const user = await lookupTeamMember(org, email);

        let users = list.users || [];
        if (!users.includes(user.uid)) {
          users = [...users, user.uid];
        }

        const update = {
          users
        };

        await updateDoc(doc(getFirestore(), 'orgs', org, 'lists', list.id), update);
        return {
          ...list,
          ...update
        };
      }
      catch(err) {
        return rejectWithValue(err);
      }
    }
);

export const popList = createAsyncThunk(
  'list/pop',
  async ({list, appendToCart}, {dispatch, getState, rejectWithValue}) => {
    try {
      if (!appendToCart) dispatch(clearCart());
      await dispatch(addCompIdsToCart(list.comps));
    }
    catch(err) {
      return rejectWithValue(err);
    }
  }
);

export const pageLists = createAsyncThunk(
  'list/page',
  async (forward, {getState, rejectWithValue}) => {
    const org = getState().auth.org.id;
    const list = getState().list.list;

    try {
      const newPage = list.page+(forward ? 1 : -1);
      const constraints = [
        orderBy(list.sort.field, list.sort.order),
        limit(list.size)
      ];

      for (const f of list.filters) {
        constraints.push(where(f.field, f.op, f.value));
      }

      if (list.page > -1) {
        if (forward) {
          constraints.push(startAfter(list.last));
        }
        else {
          constraints.push(startAt(last(list.stack)));
        }
      }

      const result = await getDocs(query(collection(getFirestore(), 'orgs', org, 'lists'), ...constraints));
      return {page: newPage, docs: result.docs}
    }
    catch(err) {
      return rejectWithValue(err);
    }
  }
);

export const openList = createAsyncThunk(
  'list/open',
  async (id, {getState, rejectWithValue}) => {
    const org = getState().auth.org.id;

    try {
      const d = await getDoc(doc(getFirestore(), 'orgs', org, 'lists', id));
      return List.create(d);
    }
    catch(err) {
      return rejectWithValue(err);
    }
  }
);

export const refreshList = createAsyncThunk(
  'list/refresh',
  async (id, {getState, rejectWithValue}) => {
    const org = getState().auth.org.id;

    try {
      const listRef = doc(getFirestore(), 'orgs', org, 'lists', id);
      const list = await getDoc(listRef);
      const compIds = list.get('comps');
      const comps = await Promise.all(compIds.map(compId => getDoc(doc(getFirestore(), 'orgs', org, 'comps', compId))));
      const liveCompIds = comps.filter(c => c.exists).map(c => c.id);
      await updateDoc(listRef, {comps: liveCompIds, updated: new Date()});
      return {comps: liveCompIds};
    }
    catch(err) {
      return rejectWithValue(err);
    }
  }
);

export const deleteFromList = createAsyncThunk(
  'list/deleteFrom',
  async ({id, compId}, {getState, rejectWithValue}) => {
    const org = getState().auth.org.id;

    try {
      const ref = doc(getFirestore(), 'orgs', org, 'lists', id);
      const d = await getDoc(ref);
      await updateDoc(ref, {
        comps: (d.get('comps')||[]).filter(it => it !== compId)
      });
      return {id, compId};
    }
    catch(err) {
      return rejectWithValue(err);
    }
  }
);

export const loadUsers = createAsyncThunk(
    'list/users/load',
    async (arg, {getState, rejectWithValue}) => {
      const org = getState().auth.org.id;
      try {
        const result = await getDocs(query(
            collection(getFirestore(), 'orgs', org, 'users'),
            orderBy("first_name", "asc"),
            limit(25),
        ));
        return result.docs.map(User.create)
      }
      catch(err) {
        return rejectWithValue(err);
      }
    }
);
const list = createSlice({
  name: 'list',
  initialState: {
    all: [],
    list: {
      page: -1,       // page index
      size: 10,       // page size
      values: [],     // page contents
      last: null,
      stack: [],
      sort: {
        field: "updated",
        order: "desc"
      },
      filters: []
    },
    mode: MODE_CREATE,
    curr: {
      title: "",
      description: ""
    },
  },
  reducers: {
    chooseList: (state, action) => {
      state.curr = (state.all||[]).find(it => it.id === action.payload);
    },
    sortLists: (state, action) => {
      const {field, order} = action.payload;
      state.list.page = -1;
      state.list.sort = {field: field.path, order}
    },

    refreshLists: (state, action) => {
      state.list.page = -1;
    },

    setMode: (state, action) => {
      state.mode = action.payload;
    },

    setProp: (state, action) => {
      const {key, value} = action.payload;
      state.curr[key] = value;

      if (state.list.values) {
        const i = state.list.values.findIndex(it => it.id === state.curr.id);
        if (i > -1) {
          state.list.values[i][key] = value;
        }
      }
    },

    setFilter: (state, action) => {
      const {field, value, op} = action.payload;
      state.list.page = -1;
      state.list.filters = (state.list.filters||[]).filter(f => f.field !== field.path);
      state.list.filters.push({
        field: field.path,
        value, op
      });
    },

    removeFilter: (state, action) => {
      state.list.filters = state.list.filters.filter(it => it.field !== action.payload.path);
      state.list.page = -1;
    },
  },
  extraReducers: builder =>
    builder
      .addCase(loadLists.fulfilled, (state, action) => {
        state.all = action.payload;
      })
      .addCase(addList.fulfilled, (state, action) => {
        state.list.page = -1;
      })
      .addCase(saveList.fulfilled, (state, action) => {
        state.list.page = -1;
      })
      .addCase(updateList.fulfilled, (state, action) => {
        state.list.page = -1;
      })
      .addCase(deleteList.fulfilled, (state, action) => {
        state.list.values = (state.list.values||[]).filter(it => it.id !== action.payload.id);
      })
      .addCase(pageLists.fulfilled, (state, action) => {
        const {page, docs} = action.payload;
        const list = state.list;
        const forward = page > state.list.page;
        const stack = list.stack.slice();
        if (forward) {
          stack.push(list.first);
        }
        else {
          stack.pop();
        }
        state.list.page = page;
        state.list.values = docs.map(doc => List.create(doc));
        state.list.last = last(docs);
        state.list.first = first(docs);
        state.list.stack = stack;
      })
      .addCase(openList.fulfilled, (state, action) => {
        state.curr = action.payload;
      })
      .addCase(deleteFromList.fulfilled, (state, action) => {
        const {id, compId} = action.payload;
        state.list.page = -1;
        if (state.curr && state.curr.id === id) {
          state.curr.comps = (state.curr.comps||[]).filter(c => c !== compId);
        }
      })
      .addCase(refreshList.fulfilled, (state, action) => {
        if (state.curr) {
          state.curr.comps = action.payload;
        }
      })
      .addCase(assignList.fulfilled, patchListInState)
      .addCase(shareList.fulfilled, patchListInState)
      .addCase(unassignList.fulfilled, patchListInState)
});

export const { chooseList, setMode, setProp, sortLists, refreshLists, setFilter, removeFilter } = list.actions;

export default list.reducer;

const patchListInState = (state, action) => {
  const mapper = it => it.id === action.payload.id ? action.payload : it
  state.list.values = state.list.values.map(mapper);
  if (state.list.all && state.list.all.length > 0) {
    state.list.all = state.list.all.map(mapper);
  }
  if (state.curr && state.curr.id === action.payload.id) {
    state.curr = action.payload;
  }
}
